import { isValid, format, addDays, subDays, setDate, setMonth, setYear, parse, addMinutes, subMinutes } from "date-fns"
import { toDate } from "date-fns-tz";
import { DateObject } from "@app/models/DateObject";

/** App date is a common object which represents a date relative to Omaha */
export class AppDate {
    /** App timezone */
    static timezone: string;

    /**
     * Standard format used for populating {@link AppDate#formatted}
     * This should be the most common format used in the system which
     * is the most performance sensitive. So, for example, "YYYY/MM/DD"
     * is the default date format for {@link https://github.com/restorando/angular-pickadate pickadate}
     */
    private static readonly FORMAT = "yyyy/MM/dd"

    /**
     * Formatted according to {@link AppDate.format}
     * This formatted version is used for caching calls to {@link AppDate#format} which
     * are made for the same format as {@link AppDate#formatted}.
     */
    private readonly formatted: string

    readonly year: number
    /** Starts at index 0 */
    readonly month: number
    readonly date: number

    /** Day of the week between [0, 6] inclusive */
    readonly day: number

    readonly dayOfWeek;
    /** Time  **/
    readonly hour: number
    readonly minute: number
    readonly second: number

    /** Javascript Date **/
    readonly jsDate: Date

    /**
     * Unique for each year/month/date, which makes comparison operations and Set operations _really_ fast.
     * @example
     * AppDate.fromString("2017/12/04").index === 20171204
     */
    readonly index: number
    readonly fullIndex: number

    private constructor(date: Date) {
        if(!isValid(date)) {
            throw new Error('Invalid date');
        }

        this.jsDate = date;
        this.formatted = format(date, AppDate.FORMAT);
        this.year = date.getFullYear();
        this.month = date.getMonth();
        this.date = date.getDate();
        this.day = date.getDay();
        this.hour = date.getHours();
        this.minute = date.getMinutes();
        this.second = date.getSeconds();
        this.index = AppDate.createIndex(this.year, this.month, this.date);
        this.fullIndex = AppDate.createFullIndex(this.year, this.month, this.date, this.hour, this.minute, this.second);
    }

    format(formatString: string): string {
        // if this was already formatted this way, just return our formatted version
        if (formatString === AppDate.FORMAT) return this.formatted

        // TODO: Remove this line when we are no longer using AppDateAjs
        formatString = formatString.replace(/mm/i, "MM");

        return format(this.jsDate, formatString);
    }

    addDays(count: number): AppDate {
        return new AppDate(addDays(this.jsDate, count));
    }

    subtractDays(count: number): AppDate {
        return new AppDate(subDays(this.jsDate, count));
    }

    addMinutes(count: number): AppDate {
        return new AppDate(addMinutes(this.jsDate, count));
    }

    subrtactMinutes(count: number): AppDate {
        return new AppDate(subMinutes(this.jsDate, count));
    }

    withDate(date: number): AppDate {
        return new AppDate(setDate(this.jsDate, date));
    }

    withMonth(month: number): AppDate {
        return new AppDate(setMonth(this.jsDate, month));
    }

    withYear(year: number): AppDate {
        return new AppDate(setYear(this.jsDate, year));
    }

    isAfter(date: AppDate): boolean {
        return this.index > date.index
    }

    isBefore(date: AppDate): boolean {
        return this.index < date.index
    }

    equals(date: AppDate): boolean {
        return date != null && this.index === date.index
    }

    // Constructors

    static fromDate(date: Date): AppDate {
        return new AppDate(toDate(date, { timeZone: AppDate.timezone }));
    }

    static now(): AppDate {
        return AppDate.fromDate(new Date());
    }

    static fromString(date: string, format: string): AppDate {

        if (date.length !== format.length) {

            if (date.length === 8 && format.length === 10) {
                format = format.replace(/yyyy/i, "yy")
            }

        }

        return AppDate.fromDate(parse(date, format, new Date()));
    }

    /** Useful for creating your own equivalent {@link AppDate#index} which aids in very fast comparison and Set/Map indexing */
    static createIndex(year: number, month: number, date: number): number {
        return year * 1e4 + month * 1e2 + date
    }

    static createFullIndex(year: number, month: number, date: number, hour: number, minute: number, second: number): number {
        return year * 1e10 + (month + 1) * 1e8 + date * 1e6 + hour * 1e4 + minute * 1e2 + second;
    }

    /** Convert the date object to the date in app timezone */
    static dateFromDateObject(dateObj: DateObject): Date {
        return this.parseDateObject(dateObj.formattedFullDate, "MM/dd/yyyy")
    }

    static parseDateObject(date: string, format: string = "yyyy/MM/dd"): Date {
        const dateFromString = AppDate.fromString(date, format);
        const parsedDate = new Date(dateFromString.year,
            dateFromString.month,
            dateFromString.date,
            dateFromString.hour,
            dateFromString.minute,
            dateFromString.second)
        parsedDate.hoapassTz = true
        return parsedDate
    }

}
